import { useEffect, useState } from "react";
import { X, AlertTriangle, TrendingDown, TrendingUp } from "lucide-react";
import { Spinner, fmtBRL, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";

const CAT_LABEL = { cx1: "CX1", cx2: "CX2", cx3: "CX3", cx3g: "CX3G", cx4: "CX4", granel: "Granel (kg)" };

export const InvoicePnlDrill = ({ invoiceId, onClose }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null); setErr("");
    api.get(`/reports/pnl/by-invoice/${invoiceId}/detail`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)));
  }, [invoiceId]);

  const r = data?.resumo || {};
  const c = data?.custos || {};
  const custoRows = data ? [
    ["Mercadoria (produtor, c/ Funrural)", c.mercadoria_bruta],
    ["Frete de entrada", c.frete_total],
    ["Maquinação", c.maquinacao_total],
    ["Sacaria", c.sacaria_total],
    ["Chapa/Descarga", c.chapa],
    ["ICMS/Seguro", c.icms_seguro],
  ].filter(([, v]) => v > 0) : [];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={onClose} data-testid="invoice-pnl-drill-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-4" onClick={(e) => e.stopPropagation()} data-testid="invoice-pnl-drill-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-tertiary sticky top-0 bg-white rounded-t-xl z-10">
          <div>
            <h2 className="font-heading text-lg font-bold text-ink-900">
              {data ? <>Raio-X da NF #{data.invoice.nota_numero}</> : "Carregando..."}
            </h2>
            {data && (
              <p className="text-xs text-ink-500">
                {data.invoice.fornecedor} · <span className="capitalize">{data.invoice.tipo_cebola}</span> · {data.invoice.data}
                {data.invoice.unidade ? ` · ${data.invoice.unidade}` : ""}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-stone-100 text-ink-500" data-testid="invoice-pnl-drill-close">
            <X size={18} />
          </button>
        </div>

        {err && <div className="m-6 text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
        {!data && !err && <div className="flex justify-center py-16"><Spinner size={28} /></div>}

        {data && (
          <div className="p-6 space-y-5">
            {(data.diagnostico || []).length > 0 && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200 space-y-1.5" data-testid="drill-diagnostico">
                <div className="flex items-center gap-2 text-sm font-bold text-danger">
                  <AlertTriangle size={15} /> Por que esse resultado?
                </div>
                {data.diagnostico.map((d, i) => (
                  <div key={i} className="text-xs text-red-900 leading-relaxed">• {d}</div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <DrillKpi label="Receita líquida" value={fmtBRL(r.receita_liquida)} hint={`venda média ${fmtBRL(r.preco_medio_venda)}/${data.invoice.unidade_medida || "cx"}`} />
              <DrillKpi label="CMV alocado" value={fmtBRL(r.cmv_alocado)} hint={`custo ${fmtBRL(r.custo_por_caixa)}/${data.invoice.unidade_medida || "cx"}`} />
              <DrillKpi label="Lucro" value={fmtBRL(r.lucro)} tone={r.lucro >= 0 ? "ok" : "danger"} hint={`margem ${r.margem_pct}%`} />
              <DrillKpi label="Vendido" value={`${r.pct_vendido}%`} hint={`${fmtNum(r.cx_vendidas, 0)} de ${fmtNum(c.total_caixas_benef, 0)} ${data.invoice.unidade_medida || "cx"}`} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="border border-tertiary rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-stone-50 border-b border-tertiary text-xs font-bold uppercase tracking-wider text-ink-500">
                  Composição do custo total
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {custoRows.map(([label, val]) => (
                      <tr key={label} className="border-b border-tertiary last:border-0">
                        <td className="px-4 py-2 text-ink-700">{label}</td>
                        <td className="px-4 py-2 text-right font-mono">{fmtBRL(val)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-ink-500 w-14">
                          {c.custo_total > 0 ? `${((val / c.custo_total) * 100).toFixed(0)}%` : ""}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-stone-50 font-bold border-t-2 border-ink-900">
                      <td className="px-4 py-2.5 text-ink-900">Custo total</td>
                      <td className="px-4 py-2.5 text-right font-mono" data-testid="drill-custo-total">{fmtBRL(c.custo_total)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs text-ink-500">100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="border border-tertiary rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-stone-50 border-b border-tertiary text-xs font-bold uppercase tracking-wider text-ink-500">
                  Produção do beneficiamento {data.invoice.modo_precificacao === "balancao" ? "(Balanção)" : "(Boca de máquina)"}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase text-ink-500 border-b border-tertiary">
                      <th className="px-4 py-1.5 text-left">Categoria</th>
                      <th className="px-4 py-1.5 text-right">Qtd</th>
                      <th className="px-4 py-1.5 text-right">Preço</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.producao || []).length === 0 ? (
                      <tr><td colSpan={3} className="px-4 py-4 text-center text-xs text-ink-500">Sem categorias lançadas.</td></tr>
                    ) : data.producao.map((p) => (
                      <tr key={p.categoria} className="border-b border-tertiary last:border-0">
                        <td className="px-4 py-2 font-semibold uppercase">{CAT_LABEL[p.categoria] || p.categoria}</td>
                        <td className="px-4 py-2 text-right font-mono">{fmtNum(p.quantidade, 0)}</td>
                        <td className="px-4 py-2 text-right font-mono">{fmtBRL(p.preco)}</td>
                      </tr>
                    ))}
                    {data.invoice.quebra_pct > 0 && (
                      <tr className="bg-amber-50">
                        <td className="px-4 py-2 text-xs text-amber-900" colSpan={3}>
                          Quebra no beneficiamento: <strong>{data.invoice.quebra_pct}%</strong>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border border-tertiary rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 bg-stone-50 border-b border-tertiary text-xs font-bold uppercase tracking-wider text-ink-500">
                Vendas desta carga ({(data.vendas || []).length}) — linhas em vermelho saíram abaixo do custo
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase text-ink-500 border-b border-tertiary">
                      <th className="px-4 py-2 text-left">Romaneio</th>
                      <th className="px-4 py-2 text-left">Data</th>
                      <th className="px-4 py-2 text-left">Cliente</th>
                      <th className="px-4 py-2 text-right">Qtd</th>
                      <th className="px-4 py-2 text-right">Preço médio/cx</th>
                      <th className="px-4 py-2 text-right">Bruto</th>
                      <th className="px-4 py-2 text-right">Deduções</th>
                      <th className="px-4 py-2 text-right">Líquido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.vendas || []).length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-5 text-center text-xs text-ink-500">Nenhuma saída finalizada usa esta NF.</td></tr>
                    ) : data.vendas.map((v, i) => {
                      const abaixo = v.caixas > 0 && v.preco_medio_cx < c.custo_por_caixa;
                      return (
                        <tr key={i} className={`border-b border-tertiary last:border-0 ${abaixo ? "bg-red-50" : ""}`} data-testid={`drill-venda-${i}`}>
                          <td className="px-4 py-2 font-mono">#{v.romaneio ?? "—"}</td>
                          <td className="px-4 py-2 font-mono text-xs">{v.data}</td>
                          <td className="px-4 py-2 truncate max-w-[180px]">{v.cliente}</td>
                          <td className="px-4 py-2 text-right font-mono">{v.caixas > 0 ? `${fmtNum(v.caixas, 0)} cx` : `${fmtNum(v.kg_granel, 0)} kg`}</td>
                          <td className={`px-4 py-2 text-right font-mono font-semibold ${abaixo ? "text-danger" : ""}`}>
                            <span className="inline-flex items-center gap-1">
                              {abaixo ? <TrendingDown size={11} /> : <TrendingUp size={11} className="text-ok" />}
                              {v.caixas > 0 ? fmtBRL(v.preco_medio_cx) : "—"}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right font-mono">{fmtBRL(v.valor_bruto)}</td>
                          <td className="px-4 py-2 text-right font-mono text-ink-500">{fmtBRL(v.deducoes)}</td>
                          <td className="px-4 py-2 text-right font-mono">{fmtBRL(v.receita_liquida)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function DrillKpi({ label, value, hint, tone = "default" }) {
  const tones = { default: "text-ink-900", ok: "text-ok", danger: "text-danger" };
  return (
    <div className="border border-tertiary rounded-lg p-3.5">
      <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-500">{label}</div>
      <div className={`font-mono text-lg font-bold mt-0.5 ${tones[tone]}`}>{value}</div>
      {hint && <div className="text-[11px] text-ink-500 mt-0.5">{hint}</div>}
    </div>
  );
}
